import {html, type TemplateResult} from "lit";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import type {FormFieldConfig} from "./formConfig";
import {ifDefined} from "lit/directives/if-defined.js";

// eslint-disable-next-line complexity
export function renderField(field: FormFieldConfig): TemplateResult {
    const baseClasses = `form-element${field.width ? " " + field.width : ""}`;

    // eslint-disable-next-line default-case
    switch (field.type) {
        case "text-field":
            return html`
                <eop-form-text-field
                    class="${baseClasses} form-element-vertically-aligned"
                    input-id=${field.id}
                    label=${field.label}
                    placeholder=${field.placeholder}
                    preset=${field.preset}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-text-field>`;

        case "text-area":
            return html`
                <eop-form-text-area
                    class=${baseClasses}
                    input-id=${field.id}
                    label=${field.label}
                    placeholder=${field.placeholder}
                    preset=${field.preset}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-text-area>`;

        case "dropdown":
            return html`
                <eop-form-dropdown
                    class="${baseClasses} form-element-vertically-aligned"
                    input-id=${field.id}
                    label=${field.label}
                    placeholder=${field.placeholder}
                    .options=${field.options ?? []}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-dropdown>`;

        case "radio-buttons":
            return html`
                <eop-form-radio-buttons
                    class="${field.style} ${baseClasses}"
                    input-id=${field.id}
                    label=${field.label}
                    .options=${field.options ?? []}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-radio-buttons>`;

        case "checkbox":
            return html`
                <eop-form-checkbox
                    class=${baseClasses}
                    input-id=${field.id}
                    label=${field.label}
                    top-label=${field.topLabel}
                    label-check=${ifDefined(field.labelChecked)}
                    label-uncheck=${ifDefined(field.labelUnchecked)}
                    ?preselected=${field.preselected ?? false}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                ></eop-form-checkbox>`;

        case "checkbox-group":
            return html`
                <eop-form-checkbox-group
                    class=${baseClasses}
                    input-id=${field.id}
                    label=${field.label}
                    .options=${field.options ?? []}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                ></eop-form-checkbox-group>`;

        case "date-field":
            return html`
                <eop-form-date-field
                    class="${baseClasses} form-element-vertically-aligned"
                    input-id=${field.id}
                    label=${field.label}
                    preset=${field.preset}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-date-field>`;

        case "upload":
            return html`
                <eop-form-upload-field
                    class=${baseClasses}
                    input-id=${field.id}
                    label=${field.label}
                    config-id=${field.configId}
                    supported-file-types=${field.supportedFileTypes}
                    max-uploads=${field.maxUploads}
                    condition=${field.condition}
                    .validations=${field.validations ?? []}
                    label-suffix-popover-id=${field.labelSuffixPopoverId}
                ></eop-form-upload-field>`;

        case "hidden":
            return html`
                <eop-form-hidden-field
                    class=${baseClasses}
                    input-id=${field.id}
                ></eop-form-hidden-field>`;

        case "toggle-link":
            return html`
                <eop-form-toggle-link
                    class=${baseClasses}
                    input-id=${field.id}
                    label-check=${field.labelCheck}
                    label-uncheck=${field.labelUncheck}
                    condition=${field.condition}
                ></eop-form-toggle-link>`;

        case "conditional-element":
            return html`
                <eop-form-conditional-element
                    class=${baseClasses}
                    condition=${field.condition}
                >${unsafeHTML(field.content)}</eop-form-conditional-element>`;
    }
}
